import { useEffect, useState } from "react";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { Button } from "@/components/ui/buttons/button";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { CaretIcon } from "@/icons";
import { useAuth } from "@/providers/auth-provider";
import { useUser } from "@/providers/user-provider";
import { getPlayerProfile } from "@/services/player/get-player-profile";
import { sendPokeshards } from "@/services/send/send-pokeshards";
import { Alert } from "@/components/alert/alert";

export const SendPokeshardsView = () => {
  const { getAccessToken } = useAuth();
  const { pokeShards, setPokeShards } = useUser();

  const [receiverName, setReceiverName] = useState<string>("");
  const [amount, setAmount] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch player data on mount
  useEffect(() => {
    fetchPlayerData();
  }, []);

  const fetchPlayerData = async () => {
    const accessToken = getAccessToken();
    if (!accessToken) return;

    try {
      const profileResponse = await getPlayerProfile(accessToken);
      if (profileResponse.ok && profileResponse.data) {
        setPokeShards(profileResponse.data.poke_shards ?? 0);
      }
    } catch (error) {
      console.error("Failed to fetch player data:", error);
    }
  };

  const handleAmountChange = (delta: number) => {
    setAmount((prev) => {
      const newAmount = prev + delta;
      if (newAmount < 1) return 1;
      return newAmount;
    });
  };

  const handleQuickAmount = (value: number | "all" | "reset") => {
    if (value === "all") {
      setAmount(pokeShards);
    } else if (value === "reset") {
      setAmount(1);
    } else {
      setAmount((prev) => prev + value);
    }
  };

  const handleSend = async () => {
    // Validation with user feedback
    if (!receiverName.trim()) {
      console.log(`receivername: ${receiverName}`);
      Alert("Please enter a receiver username");
      return;
    }

    if (amount < 1) {
      Alert("Amount must be at least 1 Pk");
      return;
    }

    if (amount > (pokeShards ?? 0)) {
      Alert(`Insufficient balance. You only have ${pokeShards} Pk`);
      return;
    }

    if (isLoading) return;

    const accessToken = getAccessToken();
    if (!accessToken) {
      Alert("Authentication error. Please try again");
      return;
    }

    setIsLoading(true);
    try {
      const response = await sendPokeshards(accessToken, {
        amount,
        receiver_name: receiverName.trim(),
      });

      if (response.ok && response.data) {
        // Update balance
        setPokeShards(response.data.current_balance);

        // Reset form
        setReceiverName("");
        setAmount(1);

        Alert(
          `Successfully sent ${response.data.amount_sent} Pk to ${receiverName}!`,
        );
      } else {
        Alert(response.message || "Failed to send pokeshards");
      }
    } catch (error) {
      console.error("❌ Send error:", error);
      Alert("An error occurred while sending pokeshards");
    } finally {
      setIsLoading(false);
    }
  };

  const canSend =
    receiverName.trim().length > 0 &&
    amount >= 1 &&
    amount <= (pokeShards ?? 0) &&
    !isLoading;

  return (
    <div className="w-full flex flex-col gap-3 pb-18 p-2 bg-pk-night-1/60 h-full backdrop-blur-[2px]">
      {/* Balance Display */}
      <div className="mt-4 min-w-1/3 ml-auto rounded-l-lg bg-pk-blue-2 z-10">
        <div className="flex items-center gap-4 w-full border-b-4 border-l-4 rounded-l-lg border-pk-blue-1 px-8 py-1">
          <img
            src="/assets/app-ui/poke-shard.png"
            alt="Shard"
            className="w-auto h-8"
          />

          <span className="text-pk-yellow-3/80 tracking-tight font-semibold text-xl">
            {pokeShards}
          </span>
        </div>
      </div>

      {/* Send Form */}
      <div className="flex flex-col mx-4">
        <div className="border-4 border-pk-gray-1">
          <div className="p-4 bg-pk-blue-3/80 border-4 border-pk-blue-4 flex flex-col gap-4">
            {/* Receiver Input */}
            <div className="flex flex-col gap-2">
              <label className="text-pk-gray-5 text-sm font-semibold">
                RECEIVER USERNAME
              </label>
              <input
                type="text"
                value={receiverName}
                onChange={(e) => setReceiverName(e.target.value)}
                placeholder="Enter username..."
                className="w-full px-4 py-2 bg-pk-blue-1 border-2 border-pk-blue-2 rounded-md text-pk-gray-5 placeholder:text-pk-gray-3 outline-none focus:border-pk-blue-4 transition"
              />
            </div>

            {/* Amount Input */}
            <div className="flex flex-col gap-2">
              <label className="text-pk-gray-5 text-sm font-semibold">
                AMOUNT
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleAmountChange(-1)}
                  disabled={amount <= 1}
                  className="px-1 group cursor-pointer py-0.5 bg-pk-blue-2 transition hover:bg-pk-blue-3 rounded-md text-pk-gray-5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CaretIcon
                    className="fill-pk-gray-4 group-hover:fill-pk-blue-5"
                    direction="down"
                  />
                </button>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setAmount(val < 1 ? 1 : val);
                  }}
                  className="w-[40px] flex-1 px-4 py-2 bg-pk-blue-1 border-2 border-pk-blue-2 rounded-md text-pk-gray-5 text-center outline-none focus:border-pk-blue-4 transition"
                />
                <button
                  onClick={() => handleAmountChange(1)}
                  className="px-1 group cursor-pointer py-0.5 bg-pk-blue-2 transition hover:bg-pk-blue-3 rounded-md text-pk-gray-5"
                >
                  <CaretIcon
                    className="fill-pk-gray-4 group-hover:fill-pk-blue-5"
                    direction="up"
                  />
                </button>
              </div>
            </div>

            {/* Quick Amount Buttons */}
            <div className="flex gap-2">
              <SimpleButton
                variant="blue"
                onClick={() => handleQuickAmount(10)}
                className="flex-1"
              >
                +10
              </SimpleButton>
              <SimpleButton
                variant="blue"
                onClick={() => handleQuickAmount(50)}
                className="flex-1"
              >
                +50
              </SimpleButton>
              <SimpleButton
                variant="blue"
                onClick={() => handleQuickAmount(100)}
                className="flex-1"
              >
                +100
              </SimpleButton>
              <SimpleButton
                variant="blue"
                onClick={() => handleQuickAmount("all")}
                className="flex-1"
              >
                ALL
              </SimpleButton>
              <SimpleButton
                variant="blue"
                onClick={() => handleQuickAmount("reset")}
                className="flex-1"
              >
                RESET
              </SimpleButton>
            </div>

            {/* Send Button */}
            <Button
              variant="blue"
              onClick={handleSend}
              disabled={!canSend}
              className="w-full"
            >
              <div className="flex items-center gap-2 justify-center">
                <img
                  src="/assets/app-ui/poke-shard.png"
                  alt="Shard"
                  className="w-auto h-8"
                />
                <span className="text-lg">
                  {isLoading ? "SENDING..." : `SEND ${amount}`}
                </span>
              </div>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
